import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";
import { trackBrevo, touchActivity } from "@/lib/brevo";
import type { NpsTriggerReason } from "@/hooks/useNpsTriggers";

interface NPSPromptProps {
  designId?: string | null;
  /** "tab" = desktop feedback tab (default, unchanged legacy behavior).
   *  "mobile-inline" = new signal-driven mobile card. */
  variant?: "tab" | "mobile-inline";
  /** Trigger reason (mobile-inline only). Prompt renders once trigger !== null. */
  trigger?: NpsTriggerReason | null;
}

const SNOOZE_KEY = "huelo:nps_snoozed_until";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function bucketFor(score: number): "promoter" | "passive" | "detractor" {
  if (score >= 9) return "promoter";
  if (score >= 7) return "passive";
  return "detractor";
}

function followUpFor(score: number): string {
  if (score >= 9) return "Great — what made it click for you?";
  if (score >= 7) return "What would push it to a 10?";
  return "What went wrong? We read every response.";
}

const NPSPrompt = ({ designId, variant = "tab", trigger = null }: NPSPromptProps) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState<boolean | null>(null);
  const [visible, setVisible] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [shownTracked, setShownTracked] = useState(false);
  const { toast } = useToast();

  const isMobileInline = variant === "mobile-inline";
  const surface = isMobileInline ? "mobile_inline" : "tab";

  // Snooze check (mobile-inline only)
  const snoozed = (() => {
    if (!isMobileInline || typeof window === "undefined") return false;
    const until = Number(window.localStorage.getItem(SNOOZE_KEY) || 0);
    return until > Date.now();
  })();

  // Load user + prior response
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid || cancelled) return;
      setUserId(uid);

      let resolvedEmail: string | null =
        userData.user?.email ??
        (userData.user?.identities?.[0]?.identity_data as any)?.email ??
        null;
      if (!resolvedEmail) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email")
          .eq("user_id", uid)
          .maybeSingle();
        resolvedEmail = profile?.email ?? null;
      }
      if (cancelled) return;
      setEmail(resolvedEmail);

      const { data, error } = await supabase
        .from("nps_responses")
        .select("id")
        .eq("user_id", uid)
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const has = !error && !!data;
      setAlreadySubmitted(has);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Decide visibility
  useEffect(() => {
    if (alreadySubmitted === null) return;
    if (alreadySubmitted) return;
    if (isMobileInline) {
      if (!trigger || snoozed) return;
    }
    if (!visible) {
      setVisible(true);
    }
  }, [alreadySubmitted, isMobileInline, trigger, snoozed, visible]);

  // Track shown once
  useEffect(() => {
    if (visible && !shownTracked) {
      setShownTracked(true);
      track("nps_shown", {
        design_id: designId ?? null,
        surface,
        trigger: trigger ?? null,
      });
    }
  }, [visible, shownTracked, designId, surface, trigger]);

  const handleDismiss = () => {
    setDismissed(true);
    track("nps_dismissed", {
      design_id: designId ?? null,
      had_score: score !== null,
      surface,
      trigger: trigger ?? null,
    });
    if (isMobileInline && typeof window !== "undefined") {
      window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    }
  };

  // Score-first commit (mobile-inline): insert row immediately on tap
  const handleScoreTap = async (n: number) => {
    if (score !== null) {
      setScore(n);
      return;
    }
    setScore(n);
    const b = bucketFor(n);
    track("nps_score_selected", {
      score: n,
      sentiment_bucket: b,
      design_id: designId ?? null,
      surface,
      trigger: trigger ?? null,
    });
    if (!isMobileInline || !userId) return;
    try {
      const { data, error } = await supabase
        .from("nps_responses")
        .insert({
          user_id: userId,
          email,
          score: n,
          comment: null,
          design_id: designId ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;
      setResponseId(data.id);
    } catch (err) {
      console.error("NPS score commit failed:", err);
    }
  };

  // Legacy tab submit
  const handleSubmitLegacy = async () => {
    if (score === null || !userId) return;
    setSubmitting(true);
    try {
      const trimmed = comment.trim().slice(0, 1000);
      const { error } = await supabase.from("nps_responses").insert({
        user_id: userId,
        email,
        score,
        comment: trimmed || null,
        design_id: designId ?? null,
      });
      if (error) throw error;
      track("nps_submitted", {
        score,
        has_comment: trimmed.length > 0,
        comment: trimmed.length > 0 ? trimmed : null,
        design_id: designId ?? null,
        surface,
      });
      void trackBrevo("survey_completed", {
        score,
        has_comment: trimmed.length > 0,
        design_id: designId ?? null,
        surface,
      });
      touchActivity();
      setSubmitted(true);
    } catch (err: any) {
      console.error("NPS submission failed:", err);
      toast({
        title: "Couldn't save your rating",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Mobile-inline step 2 submit: UPDATE the row with the comment
  const handleSubmitComment = async () => {
    if (score === null) return;
    const trimmed = comment.trim().slice(0, 1000);
    setSubmitting(true);
    try {
      if (responseId && trimmed) {
        const { error } = await supabase
          .from("nps_responses")
          .update({ comment: trimmed })
          .eq("id", responseId);
        if (error) throw error;
        track("nps_comment_added", {
          score,
          design_id: designId ?? null,
          surface,
          trigger: trigger ?? null,
        });
      }
      track("nps_submitted", {
        score,
        has_comment: trimmed.length > 0,
        comment: trimmed.length > 0 ? trimmed : null,
        design_id: designId ?? null,
        surface,
        trigger: trigger ?? null,
      });
      void trackBrevo("survey_completed", {
        score,
        has_comment: trimmed.length > 0,
        design_id: designId ?? null,
        surface,
        trigger: trigger ?? null,
      });
      touchActivity();
      setSubmitted(true);
    } catch (err: any) {
      console.error("NPS comment update failed:", err);
      toast({
        title: "Couldn't save your comment",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible || dismissed) return null;

  const headline = isMobileInline
    ? "How likely are you to recommend Huelo to a friend redesigning their space?"
    : "On a scale of 0 to 10, how likely are you to recommend Huelo to a friend or colleague?";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative p-5 sm:p-6 border border-border bg-muted/40"
    >
      {!submitted && (
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.p
            key="thanks"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-foreground"
          >
            Thanks for the feedback — it helps us make Huelo better.
          </motion.p>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="pr-6">
              <h3 className="text-sm font-semibold text-foreground">{headline}</h3>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 11 }, (_, i) => i).map((n) => {
                  const selected = score === n;
                  return (
                    <button
                      key={n}
                      onClick={() => handleScoreTap(n)}
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                        selected
                          ? "bg-selected text-selected-foreground border-transparent"
                          : "bg-background text-foreground border-border hover:border-foreground/40"
                      }`}
                      aria-label={`Score ${n}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] tracking-normal text-muted-foreground">
                <span>Not likely</span>
                <span>Very likely</span>
              </div>
            </div>

            {score !== null && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                {isMobileInline && (
                  <p className="text-xs text-muted-foreground">
                    Score saved — {followUpFor(score).toLowerCase()}
                  </p>
                )}
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder={
                    isMobileInline ? followUpFor(score) : "What's the main reason for your score? (optional)"
                  }
                  className="w-full p-3 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:border-foreground/40"
                />
                <div className="flex justify-end gap-2">
                  {isMobileInline && (
                    <button
                      onClick={() => {
                        track("nps_submitted", {
                          score,
                          has_comment: false,
                          comment: null,
                          design_id: designId ?? null,
                          surface,
                          trigger: trigger ?? null,
                          skipped_comment: true,
                        });
                        setSubmitted(true);
                      }}
                      className="px-4 py-2 text-xs font-semibold tracking-normal text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Skip
                    </button>
                  )}
                  <button
                    onClick={isMobileInline ? handleSubmitComment : handleSubmitLegacy}
                    disabled={submitting}
                    className="px-4 py-2 rounded-full text-xs font-semibold tracking-normal bg-foreground text-background hover:opacity-90 transition-opacity disabled:bg-muted disabled:text-muted-foreground"
                  >
                    {submitting ? "Sending..." : "Submit"}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default NPSPrompt;
